import { Box } from "@mui/material";
import { useParams } from "react-router";
import { DotLegalButton, DotLegalPageHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { EntityType } from "@common/Common.types";
import EntityTypeIconWithName from "@common/components/entityTypeIcon/EntityTypeIconWithName";
import { isNullOrWhitespace } from "@common/stringOperations";
import MentionInputRender from "@common/components/mentionInput/MentionInputRender";
import EntityDetailTopBar from "@common/components/entityDetailTopBar/EntityDetailTopBar";
import TopBarRow from "@common/components/entityDetailTopBar/TopBarRow";
import TopBarTile from "@common/components/entityDetailTopBar/TopBarTile";
import TopBarTextTile from "@common/components/entityDetailTopBar/TopBarTextTile";
import SegmentedControl from "@common/components/segmentedControl/SegmentedControl";
import ColoredChipsCell from "@common/components/coloredChipsCell/ColoredChipsCell";
import { useUrlProvider } from "../../useUrlProvider";
import { buildRiskAreaViews } from "../riskArea/RiskArea.types";
import { useRiskAssessmentsOverview } from "./RiskAssessmentsOverview.hooks";
import { useRiskAssessmentsOverviewStyles } from "./RiskAssessmentsOverview.styles";
import IndicatorRiskChip from "../IndicatorRiskChip";
import { useBulkMitigationSelection } from "./useBulkMitigationSelection";
import RiskEntityGraphDialog from "../riskEntityGraph/RiskEntityGraphDialog";
import RiskEntityAccessBanner from "../riskArea/RiskEntityAccessBanner";
import ScenarioAssessmentsTable from "./scenarioAssessmentsTable/ScenarioAssessmentsTable";

function RiskAssessmentsOverview() {
    const styles = useRiskAssessmentsOverviewStyles();
    const { translateString } = useTranslation();
    const { getRiskOperationalOverviewUrl } = useUrlProvider();
    const { riskAreaId, entityType, entityId } = useParams<{ riskAreaId: string; entityType: string; entityId: string }>();
    const subjectType = Number(entityType) as EntityType;

    const {
        isLoading,
        riskAreaName,
        entityName,
        description,
        getTypeLabel,
        tagChips,
        businessAreaChips,
        responsibleName,
        highestRisk,
        highestRiskIndicators,
        linkedRisk,
        isOverAppetite,
        exposureTiles,
        hasMitigatableScenarios,
        selectedView,
        onSelectView,
        entityLink,
        isEntityGraphOpen,
        openEntityGraph,
        closeEntityGraph,
    } = useRiskAssessmentsOverview(riskAreaId, subjectType, entityId);

    const bulkMitigation = useBulkMitigationSelection();

    const views = buildRiskAreaViews(translateString);

    const renderHeaderButtons = () => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <DotLegalButton buttonType="secondary" onClick={openEntityGraph}>
                {translateString("entityGraphOpen")}
            </DotLegalButton>
            {bulkMitigation.isSelectionMode ? (
                <>
                    <DotLegalButton buttonType="secondary" onClick={bulkMitigation.exitSelectionMode}>
                        {translateString("cancel")}
                    </DotLegalButton>
                    <DotLegalButton buttonType="primary" disabled={bulkMitigation.selectedCount === 0} onClick={bulkMitigation.openDialog}>
                        {`${translateString("mitigate")} (${bulkMitigation.selectedCount})`}
                    </DotLegalButton>
                </>
            ) : (
                hasMitigatableScenarios && (
                    <DotLegalButton buttonType="secondary" onClick={bulkMitigation.enterSelectionMode}>
                        {translateString("bulkMitigate")}
                    </DotLegalButton>
                )
            )}
        </Box>
    );

    return (
        <Box sx={styles.container}>
            <DotLegalPageHeader
                isLoading={isLoading}
                breadCrumbs={[
                    { name: riskAreaName ?? "", link: riskAreaId ? getRiskOperationalOverviewUrl(riskAreaId) : undefined },
                    {
                        name: `${translateString("riskAssessment")}: ${entityName}`,
                        tooltip: isNullOrWhitespace(description) ? undefined : <MentionInputRender value={description ?? ""} linkColor={"white"} />,
                    },
                ]}
                leftComponent={<SegmentedControl options={views} selected={selectedView} onSelect={(value) => onSelectView(value)} />}
                rightComponent={renderHeaderButtons()}
            />

            <RiskEntityAccessBanner riskAreaId={riskAreaId} marginBottom />
            <EntityDetailTopBar>
                <TopBarRow>
                    <TopBarTextTile label={translateString("name")} value={entityName} linkTo={entityLink} isLoading={isLoading} />
                    <TopBarTextTile
                        label={translateString("type")}
                        neverShowTooltip
                        value={<EntityTypeIconWithName type={subjectType} name={getTypeLabel(subjectType)} sx={{ justifyContent: "center" }} />}
                        isLoading={isLoading}
                    />
                    <TopBarTile label={translateString("businessArea")} isLoading={isLoading}>
                        <Box sx={styles.chipContainer}>
                            <ColoredChipsCell items={businessAreaChips} visibleCount={2} />
                        </Box>
                    </TopBarTile>
                    <TopBarTextTile label={translateString("responsible")} value={responsibleName} isLoading={isLoading} />
                    <TopBarTile label={translateString("tags")} isLoading={isLoading}>
                        <Box sx={styles.chipContainer}>
                            <ColoredChipsCell items={tagChips} visibleCount={2} />
                        </Box>
                    </TopBarTile>
                    <TopBarTile label={translateString("risk")} isLoading={isLoading}>
                        <Box sx={styles.chipContainer}>
                            {highestRisk && (
                                <IndicatorRiskChip level={highestRisk.risk} label={highestRisk.riskLabel} riskLevelIndicators={highestRiskIndicators} />
                            )}
                        </Box>
                    </TopBarTile>
                </TopBarRow>

                <TopBarRow>
                    <TopBarTile label={translateString("linkedRisk")} isLoading={isLoading}>
                        <Box sx={styles.chipContainer}>
                            {linkedRisk && (
                                <Box sx={styles.linkedRiskTileValue} onClick={openEntityGraph}>
                                    <IndicatorRiskChip
                                        level={linkedRisk.risk}
                                        label={linkedRisk.riskLabel}
                                        riskLevelIndicators={highestRiskIndicators}
                                        overAppetiteWarningText={isOverAppetite(linkedRisk.risk) ? translateString("linkedRiskOverAppetite") : undefined}
                                    />
                                </Box>
                            )}
                        </Box>
                    </TopBarTile>
                    {exposureTiles && (
                        <>
                            <TopBarTextTile label={translateString("riskExposureSettingsHeader")} value={exposureTiles.gross} isLoading={isLoading} />
                            <TopBarTextTile label={translateString("estimatedCost")} value={exposureTiles.cost} isLoading={isLoading} />
                            <TopBarTextTile
                                label={translateString("exposureAfterMitigationLabel")}
                                value={exposureTiles.afterMitigation}
                                isLoading={isLoading}
                            />
                        </>
                    )}
                </TopBarRow>
            </EntityDetailTopBar>

            {/* Keyed on the subject: the table's saved view state is read once on mount, so a different entity needs a fresh instance. */}
            {riskAreaId && entityId && (
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1, marginTop: 2 }}>
                    <ScenarioAssessmentsTable
                        key={`${riskAreaId}:${entityType}:${entityId}`}
                        riskAreaId={riskAreaId}
                        subject={{ kind: "entity", entityType: subjectType, entityId }}
                        bulkMitigation={bulkMitigation}
                    />
                </Box>
            )}

            {isEntityGraphOpen && riskAreaId && entityId && (
                <RiskEntityGraphDialog rootType={subjectType} rootId={entityId} riskAreaId={riskAreaId} onClose={closeEntityGraph} />
            )}
        </Box>
    );
}

export default RiskAssessmentsOverview;
